import AbstractPolyShape from "./AbstractPolyShape";
import { createClosingSegment } from "../support/segments/SegmentFactory";

export default class Polygon extends AbstractPolyShape {
    type = 'polygon';

    constructor(provider, data, appearance, speciesIds, label) {
        super(provider, data, appearance, speciesIds, label);
        this.close = this.p.CLOSE;
    }

    buildSegments(coordData) {
        // Check if last entry is closing segment data
        let closingData = null;
        if (coordData.length > 0 && coordData[coordData.length - 1]?.[0] === "closing") {
            closingData = coordData[coordData.length - 1];
            coordData = coordData.slice(0, -1);
        }

        let segments = super.buildSegments(coordData);
        // Add closing segment from last end back to first start
        if (segments.length > 0) {
            let lastEnd = segments[segments.length - 1].end;
            let firstStart = segments[0].start;
            let closingSeg;
            if (closingData) {
                closingSeg = createClosingSegment(
                    this.provider, lastEnd, firstStart, this, segments.length,
                    closingData[1], ...closingData.slice(2)
                );
            } else {
                closingSeg = createClosingSegment(
                    this.provider, lastEnd, firstStart, this, segments.length
                );
            }
            segments.push(closingSeg);
        }
        return segments;
    }

    clone(dx, dy) {
        return new Polygon(
            this.provider,
            {
                coordinates: this.exportCoordinates(dx, dy),
                layer: this.layer?.id,
                addedDate: this.addedDate,
                removedDate: this.removedDate,
            },
            this.appearance.clone(),
            this.speciesIds,
            this.labels.clone(),
        );
    }

    hovered() {
        if (this.isInsidePolygon()) return true;
        return this.isNearEdge();
    }

    isInsidePolygon() {
        let points = this.getFlattenedCurrentPoints();
        let passings = 0;
        for (let i = 0; i < points.length; i++) {
            let c1 = points[i];
            let c2 = points[(i + 1) % points.length];
            if (c1.y == c2.y) continue;
            if (c1.y < this.view.currentY && c2.y < this.view.currentY) continue;
            if (c1.y > this.view.currentY && c2.y > this.view.currentY) continue;
            let x = c1.x + (this.view.currentY - c1.y) * (c2.x - c1.x) / (c2.y - c1.y);
            if (x > this.view.currentX) passings++;
        }
        return passings % 2 == 1;
    }

    getFlattenedCurrentPoints() {
        let points = [];
        for (let seg of this.segments) {
            if (seg.type === 'straight') {
                if (points.length === 0) points.push({ x: seg.start.currentX, y: seg.start.currentY });
                if (seg.end !== this.segments[0].start) {
                    points.push({ x: seg.end.currentX, y: seg.end.currentY });
                }
            } else {
                if (points.length === 0) points.push({ x: seg.start.currentX, y: seg.start.currentY });
                let sampled = seg.sampleCurrentPoints(10);
                // Skip first (it's the start point) and last if it's the closing segment
                for (let j = 1; j < sampled.length; j++) {
                    if (seg.end === this.segments[0].start && j === sampled.length - 1) continue;
                    points.push(sampled[j]);
                }
            }
        }
        return points;
    }

    getFlattenedAbsPoints() {
        let points = [];
        for (let seg of this.segments) {
            if (seg.type === 'straight') {
                if (points.length === 0) points.push({ x: seg.start.absX, y: seg.start.absY });
                if (seg.end !== this.segments[0].start) {
                    points.push({ x: seg.end.absX, y: seg.end.absY });
                }
            } else {
                if (points.length === 0) points.push({ x: seg.start.absX, y: seg.start.absY });
                let sampled = seg.samplePoints(10);
                for (let j = 0; j < sampled.length; j++) {
                    if (seg.end === this.segments[0].start && j === sampled.length - 1) continue;
                    points.push(sampled[j]);
                }
            }
        }
        return points;
    }

    isNearEdge() {
        const tolerance = this.appearance.scaledStrokeWidth / 2;
        if (tolerance < 1) return false;
        const mx = this.view.currentX;
        const my = this.view.currentY;
        return this.segments.some(seg => seg.hovered(mx, my, tolerance));
    }

    export() {
        return {
            type: 'polygon',
            data: {
                coordinates: this.exportCoordinates(),
                notes: this.notes,
                addedDate: this.addedDate,
                removedDate: this.removedDate,
                layer: this.layer?.id,
                ...this.appearance.export(),
                ...this.labels.export(),
            },
            speciesIds: this.speciesIds,
        }
    }

    isPolygon() {
        return true;
    }

    get length() {
        return this.segments.length;
    }

    get displayPerimeter() {
        let total = 0;
        for (let i = 0; i < this.length; i++) {
            total += this.segmentLength(i);
        }
        return Math.round(total * 100) / 100;
    }

    get displayArea() {
        let hasCurves = this.segments.some(s => s.type !== 'straight');
        let points = hasCurves ? this.getFlattenedAbsPoints() : this.coordinates;
        const n = points.length;
        let area = 0;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            let xi = points[i].absX ?? points[i].x;
            let yi = points[i].absY ?? points[i].y;
            let xj = points[j].absX ?? points[j].x;
            let yj = points[j].absY ?? points[j].y;
            area += xi * yj;
            area -= xj * yi;
        }
        const factor = this.settings.metric ? 100 : 30.48;
        return Math.round(Math.abs(area) / 2 / (factor * factor) * 100) / 100;
    }

    get title() {
        return 'Polygon';
    }
}
